import React, { Component } from 'react';
import { Loading, Modal, Link as CarbonLink } from 'carbon-components-react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { t } from 'i18next';
import { propTypes, defaultProps, mapStateToProps, mapDispatchToProps } from './App.props';
import PortalApi from '../../services/PortalApi';
import { SET_APP_DATA, SET_APP_USER, APP_HEARTBEAT } from './App.actions';
import { SW_NEW_CONTENT } from '../../registerServiceWorker';
import { each, map } from 'lodash';
import asyncComponent from '../../components/AsyncComponent';
import { TopNav } from '../../components/TopNav';
import Reduxify from '../../services/ReduxifyController';
import { AppMenu } from '../../containers/App/AppMenu.js';
import { loadUsersnap } from '../../components/Usersnap';
import GoogleTagManager from '../../components/GoogleTagManager';
import qs from 'qs';
import { Icomoon } from '../../components/Icomoon';

const Home = asyncComponent(() => import('../Home'));
const Feed = asyncComponent(() => import('../Feed'));
const User = asyncComponent(() => import('../../controllers/User'));
const Resources = asyncComponent(() => import('../Resources'));
const Maintenance = asyncComponent(() => import('../../controllers/Maintenance'));
const NotFound = asyncComponent(() => import('../../controllers/NotFound'));
const TaxonomyPage = asyncComponent(() => import('../TaxonomyPage'));
const TopSearch = asyncComponent(() => import('../TopSearch'));
const Search = asyncComponent(() => import('../Search'));
const Setup = asyncComponent(() => import('../Setup'));
const Login = asyncComponent(() => import('../../controllers/Login'));
const ResetPassword = asyncComponent(() => import('../ResetPassword'));
const Page = asyncComponent(() => import('../../controllers/Page'));
const OrderTracking = asyncComponent(() => import('../OrderTracking'));
const LockedOut = asyncComponent(() => import('../LockedOut'));


class App extends Component {

	static propTypes = propTypes;
	static defaultProps = defaultProps;
	static mapStateToProps = mapStateToProps;
	static mapDispatchToProps = mapDispatchToProps;

	constructor(props) {
		super(props);
		PortalApi.prepare().then( () => {
			PortalApi.getPortalData().then( data => {
				this.props.dispatch({
					type: SET_APP_DATA,
					assets: data.assets || {},
					ping: data.ping,
				});
			});
			PortalApi.getUser().then( user => {
				this.props.dispatch({
					type: SET_APP_USER,
					user: user,
				})
			}).catch( e => {
				this.props.dispatch({
					type: SET_APP_USER,
					user: {isGuest: true},
				})
			});
		}).catch( e => {
			// Things is bad, we can't call the API, wonder what we should do?
			PortalApi.logger( e );
			this.props.dispatch({
				type: APP_HEARTBEAT,
				ping: false
			});
		});
		this.mainContentRef = React.createRef();
		this.handleNewContent = this.handleNewContent.bind(this);
	}

	componentDidMount() {
		document.addEventListener( SW_NEW_CONTENT, this.handleNewContent );
	}

	componentWillUnmount() {
		document.removeEventListener( SW_NEW_CONTENT, this.handleNewContent );
	}

	handleNewContent() {
		this.props.dispatch({
			type:SW_NEW_CONTENT
		});
	}

	render() {
		const { user, assets, location, ping } = this.props;

		const scrollWithOffset = (el, offset) => {
			const elPos = el.offsetTop - offset;
			window.scroll(0, elPos);
		};

		if ( ping === false ) {
			/**
			 * The system is down.  Show a Maintenance page
			 */
			if ('/system-status' === location.pathname) {
				return <div className="app">
					<Maintenance />
				</div>
			} else {
				return <Redirect to="/system-status"/>
			}
		} else if ( ping === true && '/system-status' === location.pathname ) {
			return <Redirect to="/"/>
		}

		const newContentModal = this.props.newContentAvailable ? <Modal
			open
			primaryButtonText={t('Refresh Now')}
			secondaryButtonText={t('Later')}
			onRequestClose={() => this.props.dispatch({type:SW_NEW_CONTENT,clear:true})}
			onRequestSubmit={() => window.location.reload()}
		>
			<p className="bx--modal-content__text">
				{t('Application has just been updated to the latest release. Please refresh your browser now to ensure you are running the latest version.')}
			</p>
		</Modal> : null;

		if ( user && assets ) {
			let isPage = false;
			each(assets.pages, page => {
				if ( page.path === location.pathname ) {
					isPage = true;
				}
			});

			const analytics = ( 'development' === process.env.NODE_ENV || ! assets.gtmCode ) ? '' : <GoogleTagManager gtmId={assets.gtmCode} />;
			if ( 'staging' === process.env.NODE_ENV || ( user && user.canUsersnap ) ) {
				loadUsersnap('1c26068c-f4da-434b-acf2-f689e56176df');
			}

			if ( isPage ) {
				/**
				 * A static page has been requested.  Return the routes for all of our static pages ( it doesn't seem to work to only return the relevant route ) and let the router do its thing by rendering a Page
				 */
				return (
					<div className="app">
						{newContentModal}
						{map(assets.pages, page => <Route key={page.ID} path={page.path} render={()=><Page ID={page.ID}/>} />)}
					</div>
				)
			} else if ( user.isGuest ) {
				/**
				 * We don't have a logged-in user.  If we're not on the login page, then redirect there, otherwise, render the login page
				 */
				if ( '/login' === location.pathname ) {
					return <div className="app">
						{newContentModal}
						<Login />
					</div>
				} else if ('/activate' === location.pathname || '/reset-password' === location.pathname ) {
					const resetPasswordProps = qs.parse(this.props.location.search, {ignoreQueryPrefix: true});
					return <div className="app">
						{newContentModal}
						<ResetPassword resetPasswordProps={resetPasswordProps}/>
					</div>
				} else {
					let to = {
						pathname: '/login'
					};
					if ( this.props.location.pathname !== '/' ) {
						to.search = '?redirect=' + this.props.location.pathname;
					}
					return <Redirect to={to} />
				}
			} else if ( '/setup' === location.pathname || user.requiresSetup ) {
				/**
				 * We have a user who is currently using their default password.  We need them to reset that password before doing anything
				 */
				if ( '/setup' === location.pathname ) {
					return <div className="app">
						{analytics}
						{newContentModal}
						<Setup user={user} />
					</div>
				} else {
					return <Redirect to="/setup" />
				}
			} else if ( location.pathname.match( /^\/verification/ ) || user.lockedOut ) {
				const match = location.pathname.match( /^\/verification\/?([0-9]*)/ );
				if ( match ) {
					if ( ! user.lockedOut ) {
						return <Redirect to="/"/>
					}

					return <div className="app">
						{analytics}
						{newContentModal}
						<LockedOut user={user} code={match[1]}/>
					</div>
				} else {
					return <Redirect to="/verification" />
				}
			} else {
				/**
				 * We DO have a logged-in user.  If we're on the Login page, then redirect to the home page, otherwise render the app page
				 */
				if ('/order-tracking' === location.pathname) {
					if (!user.nav_login_ids) {
						return <Redirect to="/"/>
					}
				}

				const search = <TopSearch/>;
				const resources_base = '/resources/:category(' + this.props.assets.categories.map( c => c.slug ).join('|') + ')';

				return ( '/login' === location.pathname ) ? (
					<Redirect to="/" />
				) : (
					<div className="app">
						{analytics}
						{newContentModal}
						<HashLink scroll={el => scrollWithOffset(el, 16)} to="#main-content" onClick={ () => this.mainContentRef.current.focus() } className="tbk--a11y-bar" aria-label={t('Navigation Assistant')}>
							<div className="tbk--a11y-bar__inner">{t('Skip to Content')}</div>
						</HashLink>
						<header className="app__header">
							<TopNav user={user} onClearUnread={this.props.onClearUnread} doLogout={this.props.doLogout} searchElement={search} assets={this.props.assets} currentPath={this.props.location.pathname} />
						</header>

						<div className="app__body">
							<nav className="app__body__sidenav">
								<AppMenu categories={this.props.assets.categories} MSNavID={this.props.user.nav_login_ids} />
							</nav>

							<main id="main-content" ref={this.mainContentRef} className="app__body__main" tabIndex="0">
								<Switch>
									<Route exact path="/:taxonomyPage(division|service|location)/:slug/:innerPage" component={TaxonomyPage} />
									<Route exact path="/:taxonomyPage(division|service|location)/:slug" component={TaxonomyPage} />
									<Route exact path="/dashboard" component={Home} />
									<Route exact path="/" component={Home} />
									<Route exact path={resources_base} component={Resources} />
									<Route exact path={resources_base + '/:subcategory'} component={Resources} />
									<Route path="/resources" component={Resources} />
									<Route exact path="/news/:taxonomy/:slug" component={Feed} />
									<Route exact path="/news/:taxonomy(new)" component={Feed} />
									<Route exact path="/news/:post_name" component={Feed} />
									<Route exact path="/news" component={Feed} />
									<Route path="/edit-profile" component={User} />
									<Route path="/search" component={Search} />
									<Route path="/order-tracking" component={OrderTracking} />
									<Route component={NotFound} />
								</Switch>
							</main>
						</div>
						<footer className="app__body__footer">
							<div>
								<p role="contentinfo"><strong>&copy; {(new Date().getFullYear())} Renin.</strong> {t('All rights reserved')}.</p>
								<nav className="app__body__footer__menu" aria-label={t('Legal')}>
									<CarbonLink className="tbk--page-footer__menu-link" href="https://www.renin.com/terms-conditions/" target="_blank">{t('Terms & Conditions')}</CarbonLink>
									<CarbonLink className="tbk--page-footer__menu-link" href="https://www.renin.com/privacy-policy/" target="_blank">{t('Privacy Policy')}</CarbonLink>
								</nav>
							</div>

							<nav className="app__body__footer__social" aria-label={t('Social')}>
								<CarbonLink href="https://www.facebook.com/renindoors/" target="_blank"><Icomoon icon="facebook" /><span className="sr-only">{t('Facebook')}</span></CarbonLink>
								<CarbonLink href="https://www.pinterest.ca/renindoors/" target="_blank"><Icomoon icon="pinterest" /><span className="sr-only">{t('pinterest')}</span></CarbonLink>
								<CarbonLink href="https://www.instagram.com/renindoors/" target="_blank"><Icomoon icon="instagram" /><span className="sr-only">{t('instagram')}</span></CarbonLink>
								<CarbonLink href="https://www.youtube.com/c/Renin" target="_blank"><Icomoon icon="youtube" /><span className="sr-only">{t('youtube')}</span></CarbonLink>
								<CarbonLink href="https://www.houzz.com/professionals/door-sales-and-installation/renin-pfvwus-pf~663554984" target="_blank"><Icomoon icon="houzz" /><span className="sr-only">{t('houzz')}</span></CarbonLink>
								<CarbonLink href="https://twitter.com/ReninDoors" target="_blank"><Icomoon icon="twitter" /><span className="sr-only">{t('twitter')}</span></CarbonLink>
								<CarbonLink href="https://www.linkedin.com/company/renindoors" target="_blank"><Icomoon icon="linkedin" /><span className="sr-only">{t('linkedin')}</span></CarbonLink>
							</nav>
						</footer>
					</div>
				);
			}
		} else if ( this.props.newContentAvailable ) {
			return <div className="app">
				{newContentModal}
			</div>;
		} else {
			/**
			 * We don't yet know if we have an authenticated user.  Show the loading mask
			 */
			return (
				<div className="app">
					<Loading withOverlay={true} />
				</div>
			)
		}
	}
}

export default Reduxify( App );



// WEBPACK FOOTER //
// ./src/containers/App/App.js